import { useMemo } from "react";
import Button from "../../UI/Button";
import IconButton from "../../UI/IconButton";
import Tabs from "../../UI/Tabs";
import Tab from "../../UI/Tab";
import getclassNames from "../../../utils/getClassNames";
import * as results from "../results/resultMapping";
import * as summary from "../summaries/summaryMapping";
import { useParams } from "react-router-dom";
import { useReportBuilder } from "../../../hooks/reports/useReportBuilder";
import { useModalStore } from "../../../store/report/modalStore";
import { useReportBuilderStore } from "../../../store/report/useReportBuilderStore";
import { useReportDefinitionQuery } from "../../../hooks/reports/queries/useReportQueries";
import { reportsDashboardEnabled } from "../../../utils/features";

interface ReportResultsProps {
  onRefresh: () => void;
  resultsExpanded: boolean;
  setResultsExpanded: (expanded: boolean) => void;
  setCriteriaExpanded: (expanded: boolean) => void;
  isLocked: boolean;
  isRunning: boolean;
  refreshToken?: number;
}

const ReportResults = ({
  onRefresh,
  resultsExpanded,
  setResultsExpanded,
  setCriteriaExpanded,
  isLocked,
  isRunning,
  refreshToken,
}: ReportResultsProps) => {
  const { reportId } = useParams<{ reportId: string }>();
  // custom hooks
  const { data: currentReport } = useReportDefinitionQuery(reportId ?? ""); 
  const { isDirty, getFlattenedParameters } = useReportBuilder();

  const reportType = useReportBuilderStore((s) => s.reportType);
  const openModal = useModalStore((s) => s.openModal);

  const ReportResultComponent = useMemo(
    () => results.mapping[reportType],
    [reportType]
  );
  const ReportSummaryComponent = useMemo(
    () => summary.mapping[reportType],
    [reportType]
  );

  const groupId = currentReport?.reportGroupId;
  const reportParameters = currentReport?.parameters ?? getFlattenedParameters();

  return (
    <div
      className={getclassNames(
        "report-results",
        resultsExpanded ? "expanded" : ""
      )}
    >
      <div className="report-results-header">
        <div className="row justify-content-between align-items-center">
          <div className="column-auto">
            <h2>Results</h2>
          </div>
          <div className="column-1">
            <IconButton
              id="toggle-results-expansion"
              className="small flat"
              icon={
                resultsExpanded ? (
                  <i className="ri-arrow-up-s-line"></i>
                ) : (
                  <i className="ri-arrow-down-s-line"></i>
                )
              }
              onClick={() => setResultsExpanded(!resultsExpanded)}
            />
          </div>
        </div>
      </div>
      <div className="report-results-body">
        <div className="row justify-content-end padding-2 margin-bottom-3">
          <div className="column-auto no-wrap">
            {reportsDashboardEnabled && groupId && (
              <Button
                id="add-to-dashboard"
                text="Add to Dashboard"
                className="primary outline margin-end-2"
                disabled={isLocked}
                onClick={() =>
                  openModal("addSummary", {
                    preselectedReportId: reportId ?? "",
                    reportGroupId: groupId,
                  })
                }
                icon={<i className="ri-dashboard-line"></i>}
              />
            )}
            {onRefresh && !isDirty && (
              <Button
                id="re-run-report"
                text="Refresh Report"
                className="primary margin-end-2"
                onClick={onRefresh}
                disabled={isLocked || isRunning}
                loading={isRunning}
              />
            )}
            <Button
              id="go-back"
              text="Back to Criteria"
              className="secondary"
              disabled={isLocked}
              onClick={() => {
                setResultsExpanded(false);
                setCriteriaExpanded(true);
              }}
            />
          </div>
        </div>
        <Tabs key={`${reportId}-${refreshToken ?? 0}`}>
          {ReportSummaryComponent && (
            <Tab key={"summary"} title="Summary">
              <ReportSummaryComponent
                key={`summary-${reportId}`}
                reportId={reportId!}
                parameters={reportParameters}
              />
            </Tab>
          )}
          {ReportResultComponent && (
            <Tab key={"detail"} title="Detail">
              <ReportResultComponent
                key={`detail-${reportId}`}
                reportId={reportId!}
                parameters={reportParameters}
                refreshToken={refreshToken}
              />
            </Tab>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ReportResults;
